import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiTrash2, FiPlus, FiRefreshCw } from "react-icons/fi";

/* AI TEST API */
import {
  useGetMyTestsQuery as useGetMyAiTestsQuery,
  useDeleteMyTestMutation as useDeleteAiTestMutation,
} from "../context/apiSlice";

/* FILE TEST API */
import {
  useGetMyTestsQuery as useGetMyFileTestsQuery,
  useDeleteMyTestMutation as useDeleteFileTestMutation,
} from "../context/fileTestApi";

/* DATE FORMAT */
function formatDate(iso) {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return d.toLocaleString("uz-UZ", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function MyTestsPage() {
  const navigate = useNavigate();

  const aiQuery = useGetMyAiTestsQuery();
  const fileQuery = useGetMyFileTestsQuery({ limit: 50, offset: 0 });

  const [sourceFilter, setSourceFilter] = useState("all");

  const [deleteAiTest] = useDeleteAiTestMutation();
  const [deleteFileTest] = useDeleteFileTestMutation();

  /* AI TESTS */
  const aiTests = useMemo(() => {
    return (aiQuery.data?.tests || []).map((t) => ({
      id: `ai-${t.id}`,
      rawId: t.id,          // 🔥 tests.id
      source: "ai",
      title: t.title,
      grade: t.grade || "-",
      subject: t.subject || "-",
      questionCount: t.total_questions ?? t.question_count ?? "-",
      difficulty: t.difficulty,
      language: t.language,
      createdAt: t.created_at,
    }));
  }, [aiQuery.data]);

  /* FILE TESTS (PDF) */
  const fileTests = useMemo(() => {
    return (fileQuery.data?.tests || []).map((t) => ({
      id: `file-${t.id}`,
      rawId: t.id,          // 🔥 tests.id (MUHIM)
      source: "file",
      title: t.title,
      grade: "-",
      subject: "-",
      questionCount: t.questionCount,
      difficulty: t.difficulty,
      language: t.language,
      createdAt: t.createdAt,
    }));
  }, [fileQuery.data]);

  /* MERGE */
  const tests = useMemo(() => {
    return [...aiTests, ...fileTests].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    );
  }, [aiTests, fileTests]);

  const filteredTests =
    sourceFilter === "all"
      ? tests
      : tests.filter((t) => t.source === sourceFilter);

  /* OPEN TEST — 🔥 BITTA YO‘L */
  const handleOpen = (test) => {
    navigate(`/tests/${test.rawId}`);
  };

  const handleDelete = async (test) => {
    if (!window.confirm("Testni o‘chirmoqchimisiz?")) return;
    if (test.source === "ai") {
      await deleteAiTest(test.rawId);
    } else {
      await deleteFileTest(test.rawId);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-10">
      <div className="max-w-6xl mx-auto px-4">
        <h1 className="text-2xl font-semibold mb-4">
          Mening testlarim ({tests.length})
        </h1>

        <div className="flex gap-2 mb-4">
          <button onClick={() => setSourceFilter("all")}>Barchasi</button>
          <button onClick={() => setSourceFilter("ai")}>Sinf orqali</button>
          <button onClick={() => setSourceFilter("file")}>PDF orqali</button>
        </div>

        <table className="w-full bg-white border">
          <thead>
            <tr className="bg-gray-100 text-left">
              <th className="p-2">Test</th>
              <th className="p-2">Savollar</th>
              <th className="p-2">Til</th>
              <th className="p-2">Yaratilgan</th>
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody>
            {filteredTests.map((t) => (
              <tr key={t.id} className="border-t">
                <td className="p-2">
                  <button
                    onClick={() => handleOpen(t)}
                    className="font-semibold underline"
                  >
                    {t.title}{" "}
                    <span className="text-xs text-gray-400">
                      [{t.source === "ai" ? "Sinf orqali" : "PDF orqali"}]
                    </span>
                  </button>
                </td>
                <td className="p-2">#{t.questionCount}</td>
                <td className="p-2">{t.language}</td>
                <td className="p-2">{formatDate(t.createdAt)}</td>
                <td className="p-2">
                  <button onClick={() => handleDelete(t)}>
                    <FiTrash2 />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
